// WP11 (docs/decisions/2026-09-24-wp11-faktury-czesciowe.md): Polish labels
// of the invoice coverage read model (quantities only, never money). One home
// for the „Zafakturowane częściowo (x %)” marker and the „niezafakturowana
// reszta” wording used by the invoice picker, order cards and correction panel.
import type { TFunction } from 'i18next';
import { invoiceCoveragePercent } from '../../dto/invoiceCoverage';
import type { InvoiceCoverage, InvoiceCoverageLine, InvoiceCoverageState } from '../../types/invoiceCoverage';
import { formatLocalizedQuantity } from '../../utils/numberFormatting';
import { formatUnit } from '../../utils/units';

/** „33 szt.”; unknown quantity → „—” (never 0). */
export function formatCoverageQuantity(value: number | null, unit: string | null): string {
  return value == null ? '—' : `${formatLocalizedQuantity(value)} ${formatUnit(unit)}`;
}

/** Recognition month „2026-07” → „07.2026”; unknown → null. */
export function formatRecognitionMonth(month: string | null | undefined): string | null {
  const match = month?.match(/^(\d{4})-(\d{2})$/);
  return match ? `${match[2]}.${match[1]}` : month || null;
}

/** „Płyta XPS — 33 szt.” per line with a remainder; unknown remainder → name only. */
export function formatRemainderLines(lines: InvoiceCoverageLine[]): string {
  return lines
    .map((line) => {
      const name = line.name || '—';
      return line.remainingQuantity == null ? name : `${name} — ${formatCoverageQuantity(line.remainingQuantity, line.unit)}`;
    })
    .join(', ');
}

/**
 * The same list inside a sentence that ends with „.” right after it
 * („… — 200 szt. Rozliczy ją …”): the unit's abbreviation dot also ends the
 * sentence, so the list drops its own last dot (never „szt..”).
 */
export function formatRemainderLinesInSentence(lines: InvoiceCoverageLine[]): string {
  return formatRemainderLines(lines).replace(/\.$/, '');
}

/**
 * Marker of an order's coverage: PARTIAL → „Zafakturowane częściowo (x %)”
 * (without the percent when a quantity is unknown), FULL → „Zafakturowane w
 * całości”, NONE → null.
 */
export function coverageMarkerLabel(
  coverage: Pick<InvoiceCoverage, 'state' | 'lines'> | null | undefined,
  t: TFunction,
): string | null {
  if (!coverage || coverage.state === 'NONE') return null;
  if (coverage.state === 'FULL') return t('invoices:workflow.coverage.full');
  const percent = invoiceCoveragePercent({ ...coverage, invoices: [] });
  return percent == null
    ? t('invoices:workflow.coverage.partial')
    : t('invoices:workflow.coverage.partialPercent', { percent });
}

/** List-level marker (lists carry only the state, no lines). */
export function coverageStateLabel(state: InvoiceCoverageState | string | null | undefined, t: TFunction): string | null {
  if (state === 'PARTIAL') return t('invoices:workflow.coverage.partial');
  if (state === 'FULL') return t('invoices:workflow.coverage.full');
  return null;
}

